import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  useTheme,
  Stack,
  Link,
  Chip,
} from '@mui/material';
import {
  Gavel,
  Security,
  Warning,
  Shield,
  Business,
  Support,
  Email,
  Phone,
  Language,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { alpha } from '@mui/material/styles';

// Markdown components for proper rendering
const MarkdownComponents = (theme) => ({
  h1: ({ children }) => (
    <Typography 
      variant="h3" 
      component="h1" 
      sx={{ 
        mb: 3, 
        mt: 4,
        fontWeight: 600, 
        color: 'text.primary',
      }}
    >
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography 
      variant="h4" 
      component="h2" 
      sx={{ 
        mt: 6, 
        mb: 3, 
        fontWeight: 600, 
        color: 'text.primary',
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 1,
      }}
    >
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography 
      variant="h5" 
      component="h3" 
      sx={{ 
        mt: 4, 
        mb: 2, 
        fontWeight: 600, 
        color: 'text.primary',
      }}
    >
      {children}
    </Typography>
  ),
  p: ({ children }) => (
    <Typography 
      variant="body1" 
      sx={{ 
        mb: 2, 
        lineHeight: 1.7, 
        color: 'text.primary' 
      }}
    >
      {children}
    </Typography>
  ),
  strong: ({ children }) => (
    <Typography 
      component="strong" 
      sx={{ 
        fontWeight: 600, 
        color: 'text.primary' 
      }}
    >
      {children}
    </Typography>
  ),
  ul: ({ children }) => (
    <Box component="ul" sx={{ mb: 2, pl: 3, color: 'text.primary' }}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ mb: 2, pl: 3, color: 'text.primary' }}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Typography component="li" sx={{ mb: 0.5, lineHeight: 1.6 }}>
      {children}
    </Typography>
  ),
  blockquote: ({ children }) => (
    <Box
      sx={{
        borderLeft: '4px solid',
        borderColor: 'primary.main',
        backgroundColor: theme.palette.mode === 'dark' 
          ? alpha(theme.palette.primary.main, 0.08)
          : alpha(theme.palette.primary.main, 0.04),
        px: 3,
        py: 2,
        my: 3,
        borderRadius: '0 8px 8px 0',
        '& p:last-child': {
          mb: 0,
        },
      }}
    >
      {children}
    </Box>
  ),
  a: ({ href, children }) => (
    <Link
      href={href}
      target={href?.startsWith('http') ? '_blank' : undefined}
      rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
      sx={{
        color: 'primary.main',
        textDecoration: 'none',
        '&:hover': {
          textDecoration: 'underline',
        },
      }}
    >
      {children}
    </Link>
  ),
});

const TermsPage = () => {
  const theme = useTheme();

  const sections = [
    {
      id: 'acceptance',
      title: 'Acceptance of Terms',
      icon: <Gavel />,
      content: `By downloading, installing, or using PWGen-Rust software ("the Software"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the Software.

These Terms constitute a legal agreement between you and Kief Studio LLC ("Company," "we," "us," or "our") regarding your use of PWGen-Rust.`
    },
    {
      id: 'description',
      title: 'Service Description',
      icon: <Security />,
      content: `PWGen-Rust is a password management software application that:
- Stores and manages passwords locally on your device
- Uses zero-knowledge architecture for maximum security
- Employs AES-256-GCM encryption
- Is built with memory-safe Rust programming language
- Operates entirely offline (no cloud synchronization required)

The Software is provided under the Apache License, Version 2.0, with additional commercial licensing terms for enterprise use.`
    },
    {
      id: 'license-grant',
      title: 'License Grant',
      icon: <Business />,
      content: `**Individual Use:**
For personal use or within organizations with five or fewer users, the Software is licensed under the Apache License, Version 2.0. This grants you the right to:
- Use the Software for personal or business purposes
- Modify the source code
- Distribute copies of the Software
- Create derivative works

**Enterprise/Network Use:**
Use by more than five individuals or on a network requires a separate commercial license from Kief Studio LLC. Contact us for enterprise licensing terms.

**Restrictions:**
You may not:
- Remove or alter copyright notices
- Use our trademarks without permission
- Claim ownership of the Software
- Use the Software for illegal purposes`
    },
    {
      id: 'user-responsibilities',
      title: 'User Responsibilities',
      icon: <Shield />,
      content: `**Security:**
- You are responsible for maintaining the security of your master password
- Keep your device secure and updated
- Regularly backup your password vault
- Do not share your master password with others

**Compliance:**
- Use the Software in compliance with applicable laws
- Respect intellectual property rights
- Do not use the Software to store illegal content
- Follow export control regulations if applicable

**Updates:**
- Keep the Software updated for security patches
- Review release notes for important changes
- Test updates in non-production environments if applicable`
    },
    {
      id: 'disclaimers',
      title: 'Disclaimers and Warranties',
      icon: <Warning />,
      content: `**AS-IS BASIS:**
The Software is provided "AS IS" without warranties of any kind, either express or implied, including but not limited to:
- Merchantability
- Fitness for a particular purpose
- Non-infringement
- Accuracy or completeness

**NO SERVICE GUARANTEES:**
We do not guarantee that the Software will:
- Meet your specific requirements
- Operate without interruption
- Be error-free or secure
- Be compatible with your system
- Prevent data loss

**BACKUP RESPONSIBILITY:**
You are solely responsible for backing up your data. We recommend regular exports of your password vault.`
    },
    {
      id: 'limitation-liability',
      title: 'Limitation of Liability',
      icon: <Warning />,
      content: `To the maximum extent permitted by law, Kief Studio LLC shall not be liable for any:

**Direct Damages:**
- Data loss or corruption
- System downtime or interruption
- Financial losses
- Business interruption

**Indirect Damages:**
- Lost profits or revenue
- Loss of use or data
- Cost of substitute services
- Consequential or incidental damages

**Maximum Liability:**
Our total liability for any claims shall not exceed the amount you paid for the Software (which may be zero for open-source users).

This limitation applies regardless of the theory of liability (contract, tort, negligence, etc.).`
    },
    {
      id: 'indemnification',
      title: 'Indemnification',
      icon: <Shield />,
      content: `You agree to indemnify, defend, and hold harmless Kief Studio LLC, its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including reasonable attorney fees) arising from:

- Your use of the Software
- Your violation of these Terms
- Your violation of applicable laws
- Your infringement of third-party rights
- Any content you store using the Software

This indemnification survives termination of these Terms.`
    },
    {
      id: 'termination',
      title: 'Termination',
      icon: <Business />,
      content: `**Termination by You:**
You may stop using the Software at any time by uninstalling it from your devices.

**Termination by Us:**
We may terminate your license to use the Software if you:
- Violate these Terms
- Use the Software for illegal purposes
- Exceed usage limits without proper licensing

**Effect of Termination:**
Upon termination:
- Your license to use the Software ends
- You must stop using and delete the Software
- Your data remains on your device (we cannot access it)
- Sections regarding disclaimers, liability, and indemnification survive`
    },
    {
      id: 'privacy',
      title: 'Privacy and Data',
      icon: <Security />,
      content: `**Zero-Knowledge Architecture:**
- We cannot access your password vault or data
- All data is stored locally on your device
- No personal information is required for basic functionality

**Data Collection:**
- We may collect anonymous usage statistics
- Website analytics for improvement purposes
- Support communications when you contact us

**Your Control:**
- You control all your data
- Export and delete data at any time
- No cloud storage unless explicitly enabled

See our Privacy Policy for detailed information about data handling.`
    },
    {
      id: 'updates',
      title: 'Software Updates',
      icon: <Support />,
      content: `**Automatic Updates:**
- The Software may check for updates automatically
- Updates may include security patches and new features
- You can typically control update preferences

**Compatibility:**
- Updates may change system requirements
- Backward compatibility is not guaranteed
- Test updates before deploying in critical environments

**Support:**
- Support is provided on a best-effort basis
- Community support through GitHub discussions
- Commercial support may be available for enterprise customers`
    },
    {
      id: 'governing-law',
      title: 'Governing Law',
      icon: <Gavel />,
      content: `These Terms are governed by the laws of Massachusetts, United States, without regard to conflict of law principles.

**Jurisdiction:**
Any disputes arising from these Terms or your use of the Software shall be resolved in the courts of Massachusetts.

**Severability:**
If any provision of these Terms is found to be unenforceable, the remaining provisions shall remain in full force and effect.

**Entire Agreement:**
These Terms, along with our Privacy Policy and Licensing Agreement, constitute the entire agreement between you and Kief Studio LLC regarding the Software.`
    },
    {
      id: 'changes',
      title: 'Changes to Terms',
      icon: <Business />,
      content: `We may update these Terms from time to time. When we make changes:

- We will post updated Terms on our website
- We will update the "Last Updated" date
- Significant changes may be announced via our GitHub repository
- Continued use after changes constitutes acceptance

**Notification:**
For material changes, we may provide additional notice through:
- GitHub repository announcements
- Software update notifications
- Email (if you have contacted us previously)

We encourage you to review these Terms periodically.`
    }
  ];

  const contactInfo = [
    {
      label: 'Email',
      value: 'legal@kief.studio',
      icon: <Email />
    },
    {
      label: 'Phone',
      value: '+1 617 418 4700',
      icon: <Phone />
    },
    {
      label: 'Website',
      value: 'https://kief.studio',
      icon: <Language />,
      isLink: true
    }
  ];

  return (
    <Box sx={{ py: { xs: 8, md: 12 }, minHeight: '100vh' }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography
            variant="h1"
            sx={{
              fontWeight: 800,
              mb: 3,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Terms of Service
          </Typography>
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}
          >
            Legal terms governing your use of PWGen-Rust
          </Typography>
          
          <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
            <Chip
              icon={<Gavel />}
              label="Apache 2.0 License"
              color="primary"
              variant="filled"
            />
            <Chip
              icon={<Business />}
              label="Enterprise Licensing Available"
              color="secondary"
              variant="outlined"
            />
          </Stack>
        </Box>

        {/* Last Updated */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 4,
            textAlign: 'center',
            backgroundColor: theme.palette.mode === 'dark'
              ? 'rgba(25, 118, 210, 0.1)'
              : 'rgba(25, 118, 210, 0.05)',
            border: '1px solid',
            borderColor: 'primary.main',
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            <strong>Last Updated:</strong> January 2025
          </Typography>
        </Paper>

        {/* Main Content */}
        <Stack spacing={4}>
          {sections.map((section, index) => (
            <Paper
              key={section.id}
              elevation={0}
              sx={{
                p: 4,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: 2,
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{ color: 'primary.main', mr: 2 }}>
                  {section.icon}
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {section.title}
                </Typography>
              </Box>
              
              <ReactMarkdown
                components={MarkdownComponents(theme)}
                remarkPlugins={[remarkGfm]}
              >
                {section.content}
              </ReactMarkdown>
            </Paper>
          ))}
        </Stack>

        <Divider sx={{ my: 6 }} />

        {/* Contact Information */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            border: '1px solid',
            borderColor: 'primary.main',
            borderRadius: 2,
            background: theme.palette.mode === 'dark'
              ? 'rgba(25, 118, 210, 0.1)'
              : 'rgba(25, 118, 210, 0.05)',
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, textAlign: 'center' }}>
            Legal Contact
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
            If you have questions about these Terms of Service, please contact us:
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
            {contactInfo.map((item, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ color: 'primary.main' }}>
                  {item.icon}
                </Box>
                {item.isLink ? (
                  <Link
                    href={item.value}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: 'primary.main', textDecoration: 'none' }}
                  >
                    {item.value}
                  </Link>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {item.value}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default TermsPage;